/**
 * InvoiceDetailsModal — Detalhes completos de um invoice (tela de cobrança).
 * Campos da linha sem truncar + bloco de detalhes + data (JSONB).
 */
import { FaTimes, FaFileInvoiceDollar } from 'react-icons/fa';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import { formatTaxId } from '../../../../utils/taxIdUtils';

interface Props {
    invoice: any;
    onClose: () => void;
}

const formatBool = (value: boolean | null | undefined) =>
    value == null ? '-' : value ? 'Sim' : 'Não';

const Row = ({ label, value, mono = false }: { label: string; value: any; mono?: boolean }) => (
    <div className="flex justify-between py-1 border-b border-dotted border-slate-200 dark:border-slate-600 text-sm gap-4">
        <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{label}</span>
        <span className={`text-gray-900 dark:text-gray-100 text-right break-all ${mono ? 'font-mono text-xs' : ''}`}>
            {value ?? '-'}
        </span>
    </div>
);

const InvoiceDetailsModal: React.FC<Props> = ({ invoice, onClose }) => {
    const d = invoice.details || {};
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2" title={invoice.invoiceId}>
                        <FaFileInvoiceDollar className="text-blue-500" />
                        Fatura {invoice.invoiceId ? invoice.invoiceId.slice(0, 8) : ''}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="mb-4 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
                    <h3 className="text-sm font-semibold mb-2 text-blue-700 dark:text-blue-300">Cobrança</h3>
                    <Row label="Valor" value={formatBRL(invoice.amount)} />
                    {invoice.amountPaid != null && (
                        <Row label="Valor pago" value={formatBRL(invoice.amountPaid)} />
                    )}
                    <Row label="Vencimento" value={formatDateBR(invoice.dueDate)} />
                    <Row label="Status" value={invoice.statusFormatted} />
                    <Row label="Em cobrança" value={formatBool(invoice.collect)} />
                    {invoice.totalPayment != null && (
                        <Row label="Quitação total" value={formatBool(invoice.totalPayment)} />
                    )}
                    {invoice.payer && (
                        <Row label="Pagador" value={`${invoice.payer.name} — ${formatTaxId(invoice.payer.documentNumber)}`} />
                    )}
                    {invoice.payee && (
                        <Row label="Favorecido" value={`${invoice.payee.name} — ${formatTaxId(invoice.payee.documentNumber)}`} />
                    )}
                </div>

                <div className="mb-4 p-3 rounded-lg border bg-slate-50 border-slate-200 dark:bg-slate-700/30 dark:border-slate-600">
                    <h3 className="text-sm font-semibold mb-2 text-slate-700 dark:text-slate-300">Identificadores</h3>
                    <Row label="Fatura (invoice_id)" value={invoice.invoiceId} mono />
                    <Row label="Recebível (charge_id)" value={invoice.chargeId} mono />
                    {d.paidBy && <Row label="Pago por (payment)" value={d.paidBy} mono />}
                    <Row label="Operação (credit_operation_id)" value={invoice.creditOperationId} mono />
                    {invoice.boleto && <Row label="Nosso número" value={invoice.boleto.ourNumber} mono />}
                    {invoice.boleto && <Row label="Boleto (id)" value={invoice.boleto.boletoId} mono />}
                    {d.externalId && <Row label="Id na origem (migração)" value={d.externalId} mono />}
                </div>

                <div className="mb-4 p-3 rounded-lg border bg-gray-50 border-gray-200 dark:bg-gray-700/30 dark:border-gray-600">
                    <h3 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Detalhes</h3>
                    <Row label="Código IPOC" value={d.ipocCode} mono />
                    <Row label="Com regresso" value={formatBool(d.isWithRecourse)} />
                    {/* ⚠️ Vem do TÍTULO (`title_debt.subrogation`), não mais da fatura: era
                        `invoice.is_subrogation`, que dava a um título de cinco faturas cinco
                        lugares para responder a mesma pergunta. O rótulo é o mesmo; a fonte mudou,
                        e agora a resposta é a do título inteiro (AF-3674).

                        ⚠️ `invoice.` e NÃO `d.`: as duas vêm no nível raiz do payload, ao lado de
                        contrato e natureza — como tudo que vem do TÍTULO. `details` é o bloco da
                        própria fatura, e ler de lá dava "-" sempre. */}
                    <Row label="Sub-rogação" value={formatBool(invoice.subrogation)} />
                    <Row label="Jurídico" value={formatBool(invoice.judicialPetition)} />
                    <Row label="Parcela" value={d.installmentNumber != null ? `${d.installmentNumber}/${d.totalInstallments ?? '?'}` : '-'} />
                    <Row label="Título/NF" value={d.documentTitle} />
                    <Row label="Observações" value={d.notes} />
                    <Row label="Criado em" value={invoice.createdAt ? new Date(invoice.createdAt).toLocaleString('pt-BR') : '-'} />
                </div>

                {d.data && Object.keys(d.data).length > 0 && (
                    <div className="p-3 rounded-lg border bg-gray-50 border-gray-200 dark:bg-gray-700/30 dark:border-gray-600">
                        <h3 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Dados adicionais</h3>
                        <pre className="text-xs font-mono whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200 max-h-60 overflow-y-auto">
                            {JSON.stringify(d.data, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoiceDetailsModal;
